import { Logo } from '@/components/layout/Logo'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  CheckCircle2,
  Shield,
  Zap,
  Users,
  BarChart3,
  FileCheck,
  type LucideIcon,
} from 'lucide-react'
import { landingContent } from '@/lib/config/landing-content'
import { ImageWithFallback } from '@/components/common/ImageWithFallback'

interface LandingPageProps {
  onLogin: () => void
  onSignup: () => void
}

const iconMap: Record<string, LucideIcon> = {
  FileCheck,
  Zap,
  BarChart3,
  Users,
  Shield,
  CheckCircle2,
}

export function LandingPage({ onLogin, onSignup }: LandingPageProps) {
  const features = landingContent.features.items.map((item) => ({
    icon: iconMap[item.icon] || FileCheck,
    title: item.title,
    description: item.description,
  }))

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="md" />
            <div className="flex flex-col">
              <span className="text-xl font-semibold">
                {landingContent.header.brandName}
              </span>
              <span className="text-xs text-[#1976D2]">
                {landingContent.header.tagline}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onLogin}>
              {landingContent.header.ctaButtons.signIn}
            </Button>
            <Button
              className="bg-[#1976D2] hover:bg-[#1565C0]"
              onClick={onSignup}
            >
              {landingContent.header.ctaButtons.getStarted}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          <div className="text-center lg:text-left">
            <div className="inline-block px-4 py-2 bg-[#1976D2]/10 rounded-full mb-6">
              <span className="text-sm text-[#1976D2]">
                {landingContent.hero.tagline}
              </span>
            </div>
            <h1 className="text-5xl mb-6">
              {landingContent.hero.headline}
              <span className="block text-[#1976D2] mt-2">
                {landingContent.hero.headlineHighlight}
              </span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              {landingContent.hero.subheadline}
            </p>
            <div className="flex items-center justify-center lg:justify-start gap-4">
              <Button
                size="lg"
                className="bg-[#1976D2] hover:bg-[#1565C0]"
                onClick={onSignup}
              >
                {landingContent.hero.ctaButtons.primary}
              </Button>
              <Button size="lg" variant="outline" onClick={onLogin}>
                {landingContent.hero.ctaButtons.secondary}
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-8 border-white">
              <ImageWithFallback
                src={landingContent.hero.heroImage}
                alt="QxProveIt Dashboard"
                className="w-full h-auto"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-72 h-72 bg-[#1976D2]/10 rounded-full blur-3xl -z-10" />
            <div className="absolute -top-6 -left-6 w-72 h-72 bg-blue-200/30 rounded-full blur-3xl -z-10" />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-20 bg-white/50">
        <div className="text-center mb-12">
          <h2 className="text-3xl mb-4">
            {landingContent.features.sectionTitle}
          </h2>
          <p className="text-xl text-muted-foreground">
            {landingContent.features.sectionSubtitle}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {features.map((feature) => (
            <Card
              key={feature.title}
              className="border-2 hover:border-[#1976D2] transition-colors"
            >
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-[#1976D2]/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-[#1976D2]" />
                </div>
                <CardTitle>{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>{feature.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto text-center">
          {landingContent.stats.map((stat, index) => (
            <div key={index}>
              <div className="text-4xl text-[#1976D2] mb-2">{stat.value}</div>
              <div className="text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Showcase Section */}
      <section className="container mx-auto px-4 py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center mb-12">
          <h2 className="text-3xl mb-4">
            {landingContent.showcase.sectionTitle}
          </h2>
          <p className="text-xl text-muted-foreground">
            {landingContent.showcase.sectionSubtitle}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {landingContent.showcase.images.map((image, index) => (
            <div key={index} className="relative group">
              <div className="overflow-hidden rounded-xl shadow-lg border-4 border-white transform transition-transform group-hover:scale-105">
                <ImageWithFallback
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#1976D2]/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20">
        <Card className="max-w-4xl mx-auto bg-gradient-to-br from-[#1976D2] to-[#1565C0] border-0 text-white">
          <CardHeader className="text-center pb-8">
            <CardTitle className="text-3xl text-white mb-4">
              {landingContent.cta.headline}
            </CardTitle>
            <CardDescription className="text-xl text-blue-100">
              {landingContent.cta.subheadline}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button
              size="lg"
              variant="secondary"
              className="bg-white text-[#1976D2] hover:bg-blue-50"
              onClick={onSignup}
            >
              {landingContent.cta.buttonText}
            </Button>
            <p className="text-sm text-blue-100 mt-4">
              {landingContent.cta.disclaimer}
            </p>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white/80 backdrop-blur-sm mt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Logo size="sm" />
              <div className="flex flex-col">
                <span className="text-sm text-muted-foreground">
                  {landingContent.footer.copyright}
                </span>
                <span className="text-xs text-[#1976D2]">
                  {landingContent.footer.tagline}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              {landingContent.footer.links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  {link.text}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
